import { useEffect, useMemo, useRef, useState } from "react";
import { isNewClassMember, markSeen, newestCreatedAt, readLastSeen } from "../helpers/ClassMembersSeen";
import type { ClassMember } from "../networking/schoolClasses";

/**
 * Ids of members created since the teacher's previous visit to this class. The baseline is read
 * once per class on first load and stays fixed, so badges survive reloads within the visit; the
 * stored mark advances to the newest join date on every load, so members added during the visit
 * aren't announced next time.
 */
const useNewClassMembers = (classToken: string, classMembers: ClassMember[] | null): Set<string> => {
    const [baseline, setBaseline] = useState<number | null>(null);
    const baselineClassRef = useRef<string | null>(null);

    useEffect(() => {
        if (classMembers === null) return;
        if (baselineClassRef.current !== classToken) {
            baselineClassRef.current = classToken;
            setBaseline(readLastSeen(classToken));
        }
        markSeen(classToken, Math.max(readLastSeen(classToken) ?? 0, newestCreatedAt(classMembers)));
    }, [classToken, classMembers]);

    return useMemo(() => {
        const members = classMembers ?? [];
        return new Set(members.filter((member) => isNewClassMember(member, baseline)).map((member) => member.id));
    }, [classMembers, baseline]);
};

export default useNewClassMembers;
